import type { User } from "firebase/auth";

import { appCheckFetch } from "@/lib/firebase";

import type { EpisodeCommunityResponse, EpisodeReflection } from "./types";

interface ApiError {
  message?: string;
}

async function communityFetch<T>(
  user: User | null | undefined,
  path: string,
  init: RequestInit = {},
) {
  const idToken = user ? await user.getIdToken() : undefined;
  const response = await appCheckFetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  const data = (await response.json().catch(() => null)) as (T & ApiError) | null;
  if (!response.ok || !data) {
    throw new Error(data?.message ?? "No pudimos conectar con el círculo del episodio.");
  }
  return data;
}

export function listEpisodeReflections(user: User | null | undefined, episodeSlug: string) {
  const params = new URLSearchParams({ episodeSlug });
  return communityFetch<EpisodeCommunityResponse>(
    user,
    `/api/episode-community?${params.toString()}`,
  );
}

export async function saveEpisodeReflection(
  user: User,
  input: { episodeSlug: string; episodeTitle: string; content: string },
) {
  const data = await communityFetch<{ reflection: EpisodeReflection }>(
    user,
    "/api/episode-community",
    {
      method: "POST",
      body: JSON.stringify({ action: "upsert", publicConsent: true, ...input }),
    },
  );
  return data.reflection;
}

export function deleteEpisodeReflection(user: User, reflectionId: string) {
  return communityFetch<{ deleted: true; reflectionId: string }>(user, "/api/episode-community", {
    method: "POST",
    body: JSON.stringify({ action: "delete", reflectionId }),
  });
}

export function reactToEpisodeReflection(user: User, reflectionId: string) {
  return communityFetch<{ reacted: boolean; reactionCount: number }>(
    user,
    "/api/episode-community",
    {
      method: "POST",
      body: JSON.stringify({ action: "react", reflectionId }),
    },
  );
}

export function reportEpisodeReflection(
  user: User,
  reflectionId: string,
  reason: "spam" | "offensive" | "personal-data" | "other" = "other",
) {
  return communityFetch<{ alreadyReported: boolean }>(user, "/api/episode-community", {
    method: "POST",
    body: JSON.stringify({ action: "report", reflectionId, reason }),
  });
}

export async function listMyEpisodeReflections(user: User) {
  const data = await communityFetch<{ reflections: EpisodeReflection[] }>(
    user,
    "/api/episode-community?mine=1",
  );
  return data.reflections;
}

export async function listAdminEpisodeReflections(user: User) {
  const data = await communityFetch<{ reflections: EpisodeReflection[] }>(
    user,
    "/api/episode-community?admin=1",
  );
  return data.reflections;
}

export function moderateEpisodeReflection(
  user: User,
  reflectionId: string,
  status: "published" | "hidden",
) {
  return communityFetch<{ reflectionId: string; status: "published" | "hidden" }>(
    user,
    "/api/episode-community",
    {
      method: "POST",
      body: JSON.stringify({ action: "moderate", reflectionId, status }),
    },
  );
}
